import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableContainer,
    Button,
    Select,
    FormControl,
    InputLabel,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';

import { DashboardBody, DashboardHeader, FilterControls } from '../adminDashboard/components';
import LabeledDatePicker from '../../shared/LabeledDatePicker';
import useNotificationDashboard from './useNotificationDashboard';
import { MuiTablePagination } from '../../shared/muiControls';

const ManualNotificationDashboard = (props) => {
    const {
        page,
        rowsPerPage,
        isFetching,
        list,
        selected,
        headers,
        filters,
        sortKey,
        sortDir,
        totalRecordCount,
        totalUnfilteredCount,
        //hasMore, #TODO If the full dataset retrieval ever causes problems in terms of Lambda timeouts, this may need to come back.
        retrieveList,
        handleSort,
        handleFilterString,
        //loadMore, #TODO If the full dataset retrieval ever causes problems in terms of Lambda timeouts, this may need to come back.
        handlePageChange,
        handleRowsPerPageChange,
        sendEmails,
    } = useNotificationDashboard();

    const [dateTo, setDateTo] = React.useState('');
    const [dateFrom, setDateFrom] = React.useState('');
    const [modalOpen, setModalOpen] = React.useState(false);
    const [templateId, setTemplateId] = React.useState('');

    const tableRef = React.useRef();

    React.useEffect(() => {
        retrieveList?.();
    }, []);

    const handleSend = async () => {
        const success = await sendEmails(Array.from(selected), templateId);
        if (success) setModalOpen(false);
    };

    return (
        <Box>
            <FilterControls
                filters={filters}
                handleFilterChange={handleFilterString}
                isFetching={isFetching}
                hideCounter={true}
            />

            <Paper
                sx={{
                    borderRadius: '8px',
                    marginBottom: (theme) => theme.spacing(2),
                    padding: (theme) => theme.spacing(2),
                }}
            >
                <Typography sx={{ fontWeight: 700 }}>Select a date range to limit the results.</Typography>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: (theme) => theme.spacing(5),
                            '& .react-date-picker__calendar': {
                                zIndex: 3,
                            },
                        }}
                    >
                        <LabeledDatePicker
                            value={dateFrom}
                            changeHandler={(e) => {
                                console.log('DateRange', e);
                                setDateFrom(e);
                            }}
                            label="From"
                            disabled={isFetching}
                        />
                        <LabeledDatePicker
                            value={dateTo}
                            changeHandler={(e) => setDateTo(e)}
                            label="To"
                            disabled={isFetching}
                        />

                        <Button
                            sx={{ height: 'fit-content' }}
                            variant="outlined"
                            onClick={() => handleFilterString({ To: dateTo, From: dateFrom }, 'range')}
                            disabled={isFetching}
                        >
                            Set
                        </Button>
                    </Box>

                    <Box>
                        <Button
                            variant="contained"
                            onClick={() => setModalOpen(true)}
                            disabled={isFetching || selected.size === 0}
                        >
                            Choose Template
                        </Button>
                        <Typography>{selected.size} items selected.</Typography>
                    </Box>
                </Box>
            </Paper>

            <TableContainer
                ref={tableRef}
                sx={theme=> {
                    return {
                        maxHeight: '44vh',
                        overflow: 'auto',
                        [theme.breakpoints.down('sm')]: {
                            maxHeight: '50vh',
                        },
                    };
                }}
            >
                <Table
                    stickyHeader
                    size="small"
                >
                    <DashboardHeader
                        disabled={isFetching}
                        headers={headers}
                        handleSort={handleSort}
                        sortKey={sortKey}
                        sortDir={sortDir}
                    />

                    <DashboardBody
                        list={list?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                        selected={selected}
                        headers={headers}
                        isFetching={isFetching}
                    />
                </Table>
            </TableContainer>

            <Box
                sx={theme=> {
                    return {
                        position: 'fixed',
                        bottom: 0,
                        width: '100vw',
                    }
                }}
            >
                <Paper
                    sx={{
                        padding: '1rem',
                        display: 'flex',
                        borderRadius: 0,
                    }}
                    elevation={2}
                >
                    <MuiTablePagination
                        disabled={isFetching}
                        count={totalRecordCount || 0}
                        totalCount={totalUnfilteredCount || 0}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        showFirstButton={true}
                        showLastButton={true}
                    />
                </Paper>
            </Box>

            <Dialog open={modalOpen}>
                <DialogTitle>Send batch template emails</DialogTitle>
                <DialogContent>
                    <Box>
                        <Typography>
                            <Box
                                component="span"
                                sx={{ color: (theme) => theme.palette.error.main }}
                            >
                                WARNING:
                            </Box>{' '}
                            You are about to send an email to {selected.size} applicants.{' '}
                            <Box
                                component="span"
                                sx={{ color: (theme) => theme.palette.error.main }}
                            >
                                Be sure the chosen applicants are meant to receive the chosen template.
                            </Box>
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel id="template-label">Template</InputLabel>
                            <Select
                                labelId="template-label"
                                label="Template"
                                value={templateId}
                                onChange={(e) => setTemplateId(e.target.value)}
                            >
                                <MenuItem
                                    value=""
                                    sx={{ height: 36 }}
                                ></MenuItem>
                                <MenuItem value={9}>LNH Pending Acceptance</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => setModalOpen(false)}
                            disabled={isFetching}
                        >
                            <Typography>Cancel</Typography>
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleSend}
                            disabled={isFetching || templateId === ''}
                        >
                            <Typography>Save changes</Typography>
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ManualNotificationDashboard;
